import React from 'react';
import {
  ArrowsClockwise,
  CalendarBlank,
  Funnel,
  MagnifyingGlass,
} from '@phosphor-icons/react';
import { Button } from '../../components/ui/button';
import { Card, CardContent } from '../../components/ui/card';
import { Input } from '../../components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import { ACTION_LABELS, DATE_PRESETS } from './auditLogConstants';

/**
 * Date / Action / Resource / Severity filter bar plus free-text search and
 * a refresh button. Designed as a presentational component — all state
 * lives in the parent (the `useAuditLogs` hook).
 */
const AuditFilters = ({
  filters,
  loading,
  datePreset, setDatePreset,
  selectedAction, setSelectedAction,
  selectedResource, setSelectedResource,
  selectedSeverity, setSelectedSeverity,
  searchTerm, setSearchTerm,
  onSearch,
  onRefresh,
}) => (
  <Card className="border-slate-200">
    <CardContent className="pt-6">
      <div className="flex flex-col lg:flex-row gap-4">
        <div className="flex items-center gap-2">
          <CalendarBlank size={20} className="text-slate-500" />
          <Select value={datePreset} onValueChange={setDatePreset}>
            <SelectTrigger className="w-40" data-testid="date-preset-select">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {DATE_PRESETS.map((p) => (
                <SelectItem key={p.value} value={p.value}>
                  {p.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div className="flex items-center gap-2">
          <Funnel size={20} className="text-slate-500" />
          <Select value={selectedAction} onValueChange={setSelectedAction}>
            <SelectTrigger className="w-40" data-testid="action-filter">
              <SelectValue placeholder="Action" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Actions</SelectItem>
              {filters?.actions?.map((action) => (
                <SelectItem key={action} value={action}>
                  {ACTION_LABELS[action] || action}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <Select value={selectedResource} onValueChange={setSelectedResource}>
          <SelectTrigger className="w-40" data-testid="resource-filter">
            <SelectValue placeholder="Resource" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Resources</SelectItem>
            {filters?.resource_types?.map((r) => (
              <SelectItem key={r} value={r}>
                {r.charAt(0).toUpperCase() + r.slice(1)}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        <Select value={selectedSeverity} onValueChange={setSelectedSeverity}>
          <SelectTrigger className="w-36" data-testid="severity-filter">
            <SelectValue placeholder="Severity" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Severities</SelectItem>
            {filters?.severities?.map((s) => (
              <SelectItem key={s} value={s}>
                {s.charAt(0).toUpperCase() + s.slice(1)}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        <div className="flex-1 flex gap-2">
          <Input
            placeholder="Search by email, path..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && onSearch()}
            className="max-w-xs"
            data-testid="search-input"
          />
          <Button variant="outline" onClick={onSearch} data-testid="audit-search-btn">
            <MagnifyingGlass size={16} />
          </Button>
        </div>

        <Button variant="outline" onClick={onRefresh} data-testid="audit-refresh-btn">
          <ArrowsClockwise size={16} className={loading ? 'animate-spin' : ''} />
        </Button>
      </div>
    </CardContent>
  </Card>
);

export default AuditFilters;
